import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { safeSetDoc } from '../utils';
import { Plus, X } from 'lucide-react';

export default function GroceryList({ user, appId }) {
  const [items, setItems] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const debounceRef = useRef(null);
  
  const uid = user?.uid || user?.id;

  // Listen to Firestore
  useEffect(() => {
    if (!uid || !appId) return;
    const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'userGroceries', uid);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setItems(data.items || []);
      } else {
        setItems([]);
      }
      setIsLoaded(true);
    });
    return () => unsubscribe();
  }, [uid, appId]);

  const saveItems = (newItems) => {
    setItems(newItems);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!uid || !appId) return;
      const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'userGroceries', uid);
      await safeSetDoc(docRef, { items: newItems }, { merge: true });
    }, 1000);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    const newItem = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      checked: false
    };
    saveItems([...items, newItem]);
    setInputValue('');
  };

  const toggleCheck = (id) => {
    const newItems = items.map(i => i.id === id ? { ...i, checked: !i.checked } : i);
    saveItems(newItems);
  };

  const handleDelete = (id) => {
    const newItems = items.filter(i => i.id !== id);
    saveItems(newItems);
  };

  const handleUncheckAll = () => {
    const newItems = items.map(i => ({ ...i, checked: false }));
    saveItems(newItems);
  };

  if (!isLoaded) return null;

  return (
    <div className="flex flex-col border-t border-zinc-100">
      {items.some(i => i.checked) && (
        <div className="flex justify-end px-6 pt-2 pb-1 bg-white">
          <button 
            onClick={handleUncheckAll}
            className="text-[10px] font-bold uppercase tracking-wider text-blue-500 hover:text-blue-600 transition-colors"
          >
            Uncheck All
          </button>
        </div>
      )}
      <form onSubmit={handleAdd} className="px-6 py-1.5 flex items-center gap-2 border-b border-zinc-50 bg-white">
        <Plus size={16} className="text-zinc-400 flex-shrink-0" strokeWidth={3} />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Add item"
          className="w-full bg-transparent outline-none text-xs font-medium text-zinc-700 placeholder:text-zinc-300"
        />
      </form>

      <div className="flex flex-col">
        {items.map(item => (
          <div key={item.id} className="flex items-center justify-between px-6 py-1 hover:bg-zinc-50 bg-white">
            <label className="flex items-center gap-3 flex-1 cursor-pointer min-w-0">
              <input 
                type="checkbox"
                checked={item.checked}
                onChange={() => toggleCheck(item.id)}
                className="w-3.5 h-3.5 rounded border-zinc-300 text-blue-500 focus:ring-blue-500 cursor-pointer"
              />
              <span className={`text-xs font-semibold truncate flex-1 ${item.checked ? 'text-zinc-300 line-through' : 'text-zinc-900'}`}>
                {item.text}
              </span>
            </label>
            <button 
              onClick={() => handleDelete(item.id)}
              className="p-1 -mr-1 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded transition-all"
              title="Delete item"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="py-4 text-center text-zinc-400 text-xs italic font-medium">
            No items in your grocery list.
          </div>
        )}
      </div>
    </div>
  );
}
